import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { OrderBuyComponent } from "../order-buy/order-buy.component";
import { Observable, Subject, throwError } from "rxjs";
import { GridsterService } from "src/app/services/gridster.service";
import { finalize, map, switchMap, takeUntil } from "rxjs/operators";
import { MutualFund, FundDetails } from "src/app/models/mutualFund";
import { FundAccOrderModalComponent } from "../fund-acc-order-modal/fund-acc-order-modal.component";
import { FundService } from "src/app/services/fund.service";
import { ToastrService } from "ngx-toastr";
import * as XLSX from "xlsx";

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface GoalGroup {
  CustodianCode: number;
  CustodianNameE: string;
  CustodianNameA: string;
  Portfolio: any[];
  TotalMarketValue: number;
  TotalMarginMarketValue: number;
  TotalCostValue: number;
  TotalGL: number;
  TotalGLPer: number;
}

export interface SavingsCurrencyPortfolio {
  Portfolio: any[];
  TotalCostValue: number;
  TotalMarginMarketValue: number;
  TotalMarketValue: number;
  TotalGL: number;
  TotalGLPer: number | null;
  GoalsGrouped: GoalGroup[];
}

export interface SavingsPortfolioData {
  EGPPortfolio: SavingsCurrencyPortfolio;
  USDPortfolio: SavingsCurrencyPortfolio;
  RatedPortfolio: SavingsCurrencyPortfolio;
}

export interface CurrencyGroupViewModel {
  currency: string;
  goalsGrouped: GoalGroup[];
}

// ─── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: "app-user-portfolio-savings",
  templateUrl: "./user-portfolio-savings.component.html",
  styleUrls: ["./user-portfolio-savings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPortfolioSavingsComponent implements OnInit, OnDestroy {

  // ── Inputs ──────────────────────────────────────────────────────────────────
  @Input() acc: any;
  @Input() currency: any;
  @Input() refresh: any;
  /** Pass the Portfolio object from the API (EGPPortfolio / USDPortfolio / RatedPortfolio) */
  @Input() inputPortfolio: SavingsPortfolioData;

  // ── State ───────────────────────────────────────────────────────────────────
  isProfileLoading = false;
  isRequestLoading = false;
  selectedAccount: any;
  selectedFund: MutualFund;
  fundDetails: FundDetails;

  /** Tracks expanded goal rows. Key = `${currency}-${custodianCode}`, value = true when expanded. */
  expandedGoals: Record<string, boolean> = {};

  /** View-model consumed by template */
  currencyGroups: CurrencyGroupViewModel[] = [];

  private destroy$ = new Subject<void>();
  private subscriptions: any[] = [];

  // ── Column Definitions ──────────────────────────────────────────────────────
  allFilters = [
    { prop: "Qty",             name: "qty",            isChecked: true  },
    { prop: this.translate.currentLang === "en" ? "CustodianNameE" : "CustodianNameA",
                               name: "custodian",      isChecked: true  },
    { prop: "AvgCost",         name: "cost-price",     isChecked: true  },
    { prop: "CostValue",       name: "cost-value",     isChecked: true  },
    { prop: "MarketPrice",     name: "current-price",  isChecked: true  },
    { prop: "MarketValue",     name: "mkt-value",      isChecked: true  },
    { prop: "MarginPercentage",name: "Margin %",       isChecked: false },
    { prop: "MarginMarketValue",name: "MarginMarketValue", isChecked: false },
    { prop: "GL",              name: "G/L",            isChecked: true  },
    { prop: "GLPerc",          name: "G/L %",          isChecked: true  },
    { prop: "Weight",          name: "weight",         isChecked: true  },
    { prop: "SetteledQty",     name: "SetteledQty",    isChecked: false },
    { prop: "BlkSetteledQty",  name: "BlkSetteledQty", isChecked: false },
    { prop: "AvbSetteledQty",  name: "AvbSetteledQty", isChecked: false },
    { prop: "TPlusZeroQty",    name: "TPlusZeroQty",   isChecked: false },
    { prop: "BlkTPlusZeroQty", name: "BlkTPlusZeroQty",isChecked: false },
    { prop: "AvbTPlusZeroQty", name: "AvbTPlusZeroQty",isChecked: false },
    { prop: "TPlusOneQty",     name: "TPlusOneQty",    isChecked: false },
    { prop: "BlkTPlusOneQty",  name: "BlkTPlusOneQty", isChecked: false },
    { prop: "AvbTPlusOneQty",  name: "AvbTPlusOneQty", isChecked: false },
    { prop: "NormalQty",       name: "NormalQty",      isChecked: false },
    { prop: "TPlusThreeQty",   name: "TPlusThreeQty",  isChecked: false },
    { prop: "TPlusFourQty",    name: "TPlusFourQty",   isChecked: false },
    { prop: "TPlusFiveQty",    name: "TPlusFiveQty",   isChecked: false },
  ];

  columns = [...this.allFilters];

  // ── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private _gridService: GridsterService,
    private toastr: ToastrService,
    public fundService: FundService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const saved = this._gridService.getPortfolioColumns();
    if (saved) { this.columns = saved; }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.acc && this.acc) {
      this.selectedAccount = this.acc;
    }
    if (changes.inputPortfolio && this.inputPortfolio) {
      this.expandedGoals = {};   // collapse all when data reloads
      this.buildCurrencyGroups();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed ──────────────────────────────────────────────────────────────────

  get visibleColumns() {
    return this.columns.filter(c => c.isChecked);
  }

  get hasSavingsData(): boolean {
    return this.currencyGroups.some(g => g.goalsGrouped?.length > 0);
  }

  // ── Expand / Collapse ─────────────────────────────────────────────────────────

  goalKey(currency: string, custodianCode: number): string {
    return `${currency}-${custodianCode}`;
  }

  toggleGoal(currency: string, custodianCode: number): void {
    const key = this.goalKey(currency, custodianCode);
    this.expandedGoals[key] = !this.expandedGoals[key];
    this.cdr.markForCheck();
  }

  isExpanded(currency: string, custodianCode: number): boolean {
    
    return !!this.expandedGoals[this.goalKey(currency, custodianCode)];
  }

  // ── Data Transformation ───────────────────────────────────────────────────────

  /**
   * Builds the flat `currencyGroups` view-model from the raw API shape.
   * Decorates each portfolio item with `hasOrders`.
   */
  private buildCurrencyGroups(): void {
    if (!this.inputPortfolio) { this.currencyGroups = []; return; }

    const input = this.inputPortfolio as any;

    // Normalise: parent may pass the full Portfolio object OR a single-currency slice
    let egp: SavingsCurrencyPortfolio;
    let usd: SavingsCurrencyPortfolio;
    let rated: SavingsCurrencyPortfolio;

    const empty: SavingsCurrencyPortfolio = {
      Portfolio: [], TotalCostValue: 0, TotalMarginMarketValue: 0,
      TotalMarketValue: 0, TotalGL: 0, TotalGLPer: null, GoalsGrouped: [],
    };

    if (input) {
      // ✅ Correct shape: { EGPPortfolio, USDPortfolio, RatedPortfolio }
      egp   = empty  || empty;
      usd   = input  || empty;
      rated = empty  || empty;
    } else {
      // ⚠️ Parent is passing the wrong level — log what we got so it's easy to fix
      console.warn('UserPortfolioSavings: inputPortfolio does not contain EGPPortfolio/USDPortfolio/RatedPortfolio.',
        'Received keys:', Object.keys(input),
        '\nFix: pass [inputPortfolio]="portfolioApiResponse.Portfolio" not the full response or a sub-object.');
      this.currencyGroups = [];
      return;
    }

    const mapGoals = (goals: GoalGroup[] = []): GoalGroup[] =>
      goals.map(goal => ({
        ...goal,
        Portfolio: (goal.Portfolio || []).map(item => ({
          ...item,
          hasOrders:
            item.BlkSetteledQty > 0 ||
            item.BlkTPlusZeroQty > 0 ||
            item.BlkTPlusOneQty > 0,
        })),
      }));

    this.currencyGroups = [
      { currency: `${this.currency}`,   goalsGrouped: mapGoals(egp.GoalsGrouped)   },
      { currency: `${this.currency}`,   goalsGrouped: mapGoals(usd.GoalsGrouped)   },
      { currency: `${this.currency}`, goalsGrouped: mapGoals(rated.GoalsGrouped) },
    ];

  }

  // ── Order Actions ─────────────────────────────────────────────────────────────

  orderBuy(company: any, isSell: number): void {
    this.isRequestLoading = true;

    this.getSelectedFund(company.SymbolCode)
      .pipe(
        switchMap(selectedFund => {
          if (!selectedFund) {
            this.openOrderDialog(company, isSell);
            this.isRequestLoading = false;
            this.cdr.detectChanges();
            return throwError(() => new Error('No fund'));
          }
          this.selectedFund = selectedFund;
          return this.getMutualFundDetails(company.SymbolCode);
        }),
        finalize(() => {
          this.isRequestLoading = false;
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: details => {
          this.fundDetails = details;
          details ? this.openFundAccOrderModal(company, isSell) : this.openOrderDialog(company, isSell);
        },
        error: () => { this.isRequestLoading = false; },
      });
  }

  private openOrderDialog(company: any, isSell: number): void {
    this.dialog.open(OrderBuyComponent, {
      disableClose: true,
      panelClass: 'order-dialog-container',
      data: { symbol: company.SymbolCode, custodianCode: company.CustodianCode, type: isSell, currentUser: this.selectedAccount },
      direction: this.getDialogDirection(),
    });
  }

  private openFundAccOrderModal(company: any, isSell: number): void {
    this.dialog.open(FundAccOrderModalComponent, {
      panelClass: 'modal-width',
      data: { quantityAvailable: company.AvbSetteledQty, isBuy: !isSell, selectedFund: this.fundDetails, fundDetails: this.fundDetails, currentUser: this.selectedAccount },
    });
  }

  private getDialogDirection(): 'rtl' | 'ltr' {
    return this.translate.currentLang === 'ar' ? 'rtl' : 'ltr';
  }

  newOrder(): void {
    const ref = this.dialog.open(OrderBuyComponent, {
      disableClose: true,
      panelClass: 'order-dialog-container',
      data: { type: 0, currentUser: this.selectedAccount },
      direction: this.getDialogDirection(),
    });
    this.subscriptions.push(ref.afterClosed().subscribe());
  }

  // ── Fund Helpers ──────────────────────────────────────────────────────────────

  getSelectedFund(symbolCode: string): Observable<MutualFund | null> {
    return this.fundService.getMutualFundList(symbolCode)
      .pipe(map(({ selectedFund }) => selectedFund || null));
  }

  getMutualFundDetails(symbolCode: string): Observable<FundDetails | null> {
    return this.fundService.getMutualFundDetails(symbolCode).pipe(
      map(details => {
        if (details) {
          details.PdfsUrlsList = details.PdfsUrls ? details.PdfsUrls.split(',') : [];
          return details;
        }
        return null;
      }),
    );
  }

  // ── Export ────────────────────────────────────────────────────────────────────

  @ViewChild('TABLE') table: ElementRef;

  exportAsExcel(): void {
    const rows: any[] = [];

    this.currencyGroups.forEach(group => {
      group.goalsGrouped.forEach(goal => {
        goal.Portfolio.forEach(item => {
          rows.push({
            Currency:    group.currency,
            Goal:        this.translate.currentLang === 'ar' ? goal.CustodianNameA : goal.CustodianNameE,
            Symbol:      this.translate.currentLang === 'ar' ? item.SymbolNameAr   : item.SymbolNameEn,
            Qty:         item.Qty,
            AvgCost:     item.AvgCost,
            CostValue:   item.CostValue,
            MarketPrice: item.MarketPrice,
            MarketValue: item.MarketValue,
            GL:          item.GL,
            'GL%':       item.GLPerc,
            Weight:      item.Weight,
          });
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SavingsPortfolio');
    XLSX.writeFile(wb, `SavingsPortfolio_${this.acc?.relationCode}.xlsx`);

    this.toastr.success('', this.translate.instant('successDocumentDownload'), {
      timeOut: 15000,
      positionClass: 'toast-bottom-center',
      closeButton: true,
    });
  }
}
